import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Checkbox,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { describeRuleKey } from "encamp-shared/src/waste/stateWasteCodes";
import { BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";
import React from "react";

import {
  getDiscrepancyStatus,
  getStatusChipIcon,
  getStatusChipTooltip,
  getStatusColor,
  getStatusLabel,
} from "./DiscrepancyReviewDialog.helpers";
import { SECTION_TITLE_HEADER_HEIGHT } from "./DiscrepancySection";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

const SOURCE_LABELS: Array<{
  key: "eManifestValue" | "vendorValue" | "manifestDocumentValue";
  label: string;
}> = [
  { key: "eManifestValue", label: "e-Manifest" },
  { key: "vendorValue", label: "Vendor" },
  { key: "manifestDocumentValue", label: "Document" },
];

export interface FieldIssuesPaneProps {
  items: Discrepancy[];
  loading?: boolean;
  /** Selection set shared with the dialog's sticky bulk-action bar. */
  selectedIds: ReadonlySet<string>;
  /** Toggle a row's selection; shiftKey enables range-select like the field tab. */
  onToggleSelect: (id: string, opts?: { shiftKey?: boolean }) => void;
  onRowMenuOpen: (params: { anchorEl: HTMLElement; discrepancy: Discrepancy }) => void;
}

export function FieldIssuesPane({
  items,
  loading,
  selectedIds,
  onToggleSelect,
  onRowMenuOpen,
}: FieldIssuesPaneProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const stickyOpaquePanelBg = isDark
    ? {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: `linear-gradient(${theme.palette.common.white}29, ${theme.palette.common.white}29)`,
      }
    : { backgroundColor: theme.palette.background.paper };

  const COL_COUNT = 8; // checkbox, Facility, Manifest, Issue, Source(s), Value, Status, actions

  if (loading && items.length === 0) {
    return (
      <Stack spacing={2} sx={{ p: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["", "Facility", "Manifest", "Issue", "Source(s)", "Value", "Status", ""].map(
                (h, i) => (
                  <TableCell key={h || `sk-${i}`}>
                    <Skeleton variant="text" width={80} />
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 6 }, (_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 8 }, (__, j) => (
                  <TableCell key={j}>
                    <Skeleton variant="text" width={j === 1 ? 120 : 80} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    );
  }

  return (
    // White grid surface so rows don't inherit the section card's gray; the grid is the
    // card's last element, so round its bottom to match the card. "inherit" needs the
    // container to stay the section Paper's direct child — don't rewrap it.
    <TableContainer
      sx={{
        overflow: "visible",
        bgcolor: "background.paper",
        borderBottomLeftRadius: "inherit",
        borderBottomRightRadius: "inherit",
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          "& .MuiTableHead-root .MuiTableCell-head": {
            // Stack below the section's sticky title band.
            top: SECTION_TITLE_HEADER_HEIGHT,
            ...stickyOpaquePanelBg,
            borderBottom: `1px solid ${theme.palette.background.paper}`,
          },
          "& .MuiTableBody-root .MuiTableCell-body": {
            verticalAlign: "top",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Facility</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Manifest</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Issue</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Source(s)</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Value</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Status</TableCell>
            <TableCell padding="checkbox" />
          </TableRow>
        </TableHead>

        <TableBody>
          {items.map((d) => {
            const status = getDiscrepancyStatus(d);
            const ruleKey = d.ruleKey ?? null;
            const issueDesc = ruleKey != null ? describeRuleKey(ruleKey) : "—";

            // Collect non-null source values for the "Value" cell.
            const nonNullSources = SOURCE_LABELS.filter((s) => d[s.key] != null);
            const valueText = nonNullSources.map((s) => d[s.key]).join(", ");

            const tooltipText = getStatusChipTooltip(d, status);
            const statusIcon = getStatusChipIcon(d, status);
            const chip = (
              <Chip
                size="small"
                label={getStatusLabel(status)}
                color={getStatusColor(status)}
                variant="outlined"
                icon={
                  statusIcon ? React.cloneElement(statusIcon, { "aria-hidden": true }) : undefined
                }
                sx={{
                  cursor: "default",
                  mt: "2px",
                  flexDirection: "row-reverse",
                  bgcolor: "background.paper",
                  "& .MuiChip-icon": {
                    fontSize: 14,
                    opacity: 0.7,
                    marginRight: "5px",
                    marginLeft: "-2px",
                  },
                }}
              />
            );

            return (
              <TableRow key={d.id} hover selected={selectedIds.has(d.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(d.id)}
                    onChange={(e) => {
                      // shiftKey lives on the native event, not the synthetic ChangeEvent.
                      const nativeEvt = e.nativeEvent;
                      const shiftKey =
                        nativeEvt instanceof MouseEvent || nativeEvt instanceof KeyboardEvent
                          ? nativeEvt.shiftKey
                          : false;
                      onToggleSelect(d.id, { shiftKey });
                    }}
                    // Matches the Data Source Conflicts grid's top-aligned checkbox
                    // (measured in-browser: 6px lands on the first text line).
                    sx={{ mt: "6px" }}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" sx={{ pt: "4px" }}>
                    {d.facilityName ?? "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.epaId}
                  </Typography>
                </TableCell>

                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" fontWeight={500} sx={{ pt: "4px" }}>
                    {d.manifestTrackingNumber ?? d.processedManifestId.slice(0, 8)}
                  </Typography>
                  {d.wasteLineUnifiedId && (
                    <Typography variant="caption" color="text.secondary">
                      Line {d.wasteLineNumber ?? "item"}
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={{ maxWidth: 280, wordBreak: "break-word" }}>
                  <Typography variant="body2" sx={{ pt: "4px" }}>
                    {issueDesc}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ pt: "4px" }}>
                    {SOURCE_LABELS.filter((s) => d[s.key] != null).map((s) => (
                      <Chip
                        key={s.key}
                        size="small"
                        label={s.label}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 20 }}
                      />
                    ))}
                    {nonNullSources.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
                  <Typography variant="body2" sx={{ pt: "4px" }}>
                    {valueText || "—"}
                  </Typography>
                </TableCell>

                <TableCell>
                  {tooltipText ? <Tooltip title={tooltipText}>{chip}</Tooltip> : chip}
                </TableCell>

                <TableCell padding="checkbox">
                  <Tooltip title="Row actions">
                    <IconButton
                      size="small"
                      sx={{ mt: "6px" }}
                      onClick={(event) =>
                        onRowMenuOpen({ anchorEl: event.currentTarget, discrepancy: d })
                      }
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}

          {items.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={COL_COUNT}
                align="center"
                sx={{ py: 8, bgcolor: subtleSurfaceBg, borderBottom: "none" }}
              >
                <Typography variant="body2" color="text.secondary">
                  No field issues found for the selected manifest(s).
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
